import React, { useContext, useEffect, useMemo } from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import { useAccess } from '@/common/hooks/useAccess';
import { useMatch } from 'react-router-dom';
import { RootState } from '../../app/rootReducer';
import { PaginatedGrid } from '../../common/components/PaginatedGrid';
import { Resource } from '../../common/constants/resource';
import { NoraPermissionV2 } from '../../common/constants/roles';
import { UserOrganization } from '../../common/constants/userOrganization';
import {
  PickRoleForQuickFilters,
  useQuickFilters,
} from '../../common/helpers/quickFilters/quickFilters';
import { IFilter } from '../../common/interfaces/IFilter';
import { AuthContext } from '../login/AuthWrapper';
import { ReferralActionRequiredPage } from '../referral-action-required/ReferralActionRequiredPage';
import columnsResolver from './ColumnsResolver';
import { FilterBar } from './FilterBar';
import { DashboardHeader } from './Header';
import { LeftSidebarV2 } from './LeftSidebarV2';
import { OverviewPage } from './OverviewPage';
import { QAListPage } from './qa-list-page';
import styles from './styles.module.scss';
import { TaskList } from './TaskList';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export const DashboardV2 = () => {
  // Context
  const auth = useContext(AuthContext);

  // Selectors
  const decisionFilter: IFilter[] = useSelector(
    state => state.filters.decisionFilters,
  );
  const stageFilter: IFilter[] = useSelector(
    state => state.filters.stageFilters,
  );
  const lifecycleFilters: IFilter[] = useSelector(
    state => state.filters.lifecycleFilters,
  );
  const detailedLifecycleFilters: IFilter[] = useSelector(
    state => state.filters.detailedLifecycleFilters,
  );
  const forReviewFilter: IFilter[] = useSelector(
    state => state.filters.forReviewFilter,
  );
  const userFilter: IFilter[] = useSelector(state => state.filters.userFilters);
  const carrierUserFilter: IFilter[] = useSelector(
    state => state.filters.carrierUserFilter,
  );
  const productFilter: IFilter[] = useSelector(
    state => state.filters.productFilters,
  );
  const fromDate = useSelector(state => state.filters.fromDate);
  const toDate = useSelector(state => state.filters.toDate);
  const searchFilter = useSelector(state => state.filters.searchFilter);
  const agencySearchFilter = useSelector(
    state => state.filters.agencySearchFilter,
  );
  const page = useSelector(state => state.filters.page);

  const filtersInitialized = useSelector(state => state.filters.initialized);
  const lastTaskSectionIndex = useSelector(
    state => state.taskView.lastSectionIndex,
  );
  const lastTaskFilterIndex = useSelector(
    state => state.taskView.lastFilterIndex,
  );

  // application sidebar menu is not part of the filters for new layout
  // hence the filter index should be -1, to indicate that the application view is selected
  const APPLICATION_VIEW_INDEX = -1;

  const canViewTasksManagement = useAccess({
    permission: NoraPermissionV2.VIEW_TASKS_MANAGEMENT,
  });
  const pickedRoleForQuickFilters = PickRoleForQuickFilters(auth.roles);

  const { filterType, taskQuery, taskViewTitle } = useQuickFilters(
    pickedRoleForQuickFilters,
    auth.userOrganization as UserOrganization,
    lastTaskSectionIndex,
    lastTaskFilterIndex,
    canViewTasksManagement,
  );

  // Memoized functions
  const isApplicationView = useMemo(
    (): boolean =>
      lastTaskSectionIndex === APPLICATION_VIEW_INDEX &&
      lastTaskFilterIndex === APPLICATION_VIEW_INDEX,
    [lastTaskSectionIndex, lastTaskFilterIndex],
  );

  const isOverviewMatch = useMatch('/workbench/overview');
  const isOverviewView = useMemo(
    (): boolean =>
      lastTaskSectionIndex === APPLICATION_VIEW_INDEX &&
      lastTaskFilterIndex === APPLICATION_VIEW_INDEX &&
      Boolean(isOverviewMatch),
    [lastTaskSectionIndex, lastTaskFilterIndex, isOverviewMatch],
  );

  const isQAListMatch = useMatch('/workbench/qa');
  const isQAListView = useMemo(
    (): boolean =>
      lastTaskSectionIndex === APPLICATION_VIEW_INDEX &&
      lastTaskFilterIndex === APPLICATION_VIEW_INDEX &&
      Boolean(isQAListMatch),
    [lastTaskSectionIndex, lastTaskFilterIndex, isQAListMatch],
  );

  const isReferralsActionRequiredMatch = useMatch(
    '/workbench/referrals/action-required',
  );
  const isReferralsActionRequiredView = Boolean(isReferralsActionRequiredMatch);

  // Effects
  useEffect(() => {
    document.title = `${Resource.APPLICATIONS}${Resource.NORA_M_DASH}${Resource.APPLICATION_TITLE}`;
  }, []); // Empty dependency array since this only needs to run once

  return (
    <>
      <div className={styles.Layout}>
        <div className={`${styles.Column} ${styles.widthAdjust}`}>
          <LeftSidebarV2
            roles={auth.roles}
            userOrganization={auth.userOrganization}
            selectedSectionIndex={lastTaskSectionIndex}
            selectedFilterIndex={lastTaskFilterIndex}
          />

          <div className={styles.DashboardMainView}>
            {isOverviewView ? (
              <>
                <OverviewPage />
              </>
            ) : isQAListView ? (
              <>
                <QAListPage />
              </>
            ) : isReferralsActionRequiredView ? (
              <>
                <div className={styles.headerContainer}>
                  <DashboardHeader title="Referral Action Required" />
                </div>
                <ReferralActionRequiredPage embedInDashboard />
              </>
            ) : isApplicationView ? (
              <>
                <div className={styles.headerContainer}>
                  <DashboardHeader title="Applications" />
                  <FilterBar />
                </div>
                {filtersInitialized && (
                  <PaginatedGrid
                    viewName="applications"
                    columnsResolver={columnsResolver}
                    fromDate={fromDate}
                    toDate={toDate}
                    searchFilter={searchFilter}
                    agencySearchFilter={agencySearchFilter}
                    decisionFilter={decisionFilter}
                    stageFilter={stageFilter}
                    forReviewFilter={forReviewFilter}
                    userFilter={userFilter}
                    productFilter={productFilter}
                    lifecycleFilters={lifecycleFilters}
                    carrierUserFilter={carrierUserFilter}
                    detailedLifecycleFilters={detailedLifecycleFilters}
                    page={page}
                  />
                )}
              </>
            ) : (
              <>
                <DashboardHeader title={taskViewTitle} />
                <TaskList quickFilterType={filterType} taskQuery={taskQuery} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
